import React, { useEffect, useState } from 'react';
import { XIcon, CalendarIcon, ClockIcon, Trash2Icon } from 'lucide-react';
import { Coffee } from './coffeeData';
interface EditCoffeeModalProps {
  isOpen: boolean;
  onClose: () => void;
  coffee: Coffee;
  onUpdate: (updatedCoffee: Partial<Coffee>) => void;
  onDelete: () => void;
}
const EditCoffeeModal: React.FC<EditCoffeeModalProps> = ({
  isOpen,
  onClose,
  coffee,
  onUpdate,
  onDelete
}) => {
  const [title, setTitle] = useState(coffee.title);
  const [description, setDescription] = useState(coffee.description);
  const [date, setDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [platform, setPlatform] = useState(coffee.platform);
  const [meetingLink, setMeetingLink] = useState(coffee.meetingLink || '');
  const [selectedTopics, setSelectedTopics] = useState<string[]>(coffee.topics);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  useEffect(() => {
    if (coffee) {
      // Format date for date input (YYYY-MM-DD)
      const coffeeDate = new Date(coffee.date);
      setDate(coffeeDate.toISOString().split('T')[0]);
      // Format time for time inputs (HH:MM)
      setStartTime(coffeeDate.toTimeString().substring(0, 5));
      const endDate = new Date(coffee.endTime);
      setEndTime(endDate.toTimeString().substring(0, 5));
    }
  }, [coffee]);
  const topics = ['Sostenibilità', 'Innovazione', 'Economia Circolare', 'ESG', 'Energie Rinnovabili', 'Green Finance', 'Carbon Management', 'Reporting di Sostenibilità', 'Moda Sostenibile', 'Net-Zero'];
  const handleTopicToggle = (topic: string) => {
    if (selectedTopics.includes(topic)) {
      setSelectedTopics(selectedTopics.filter(t => t !== topic));
    } else {
      if (selectedTopics.length < 3) {
        setSelectedTopics([...selectedTopics, topic]);
      }
    }
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Create a new date object from date and startTime
    const [year, month, day] = date.split('-').map(Number);
    const [hours, minutes] = startTime.split(':').map(Number);
    const newDate = new Date(year, month - 1, day, hours, minutes);
    // Create a new date object for endTime
    const [endHours, endMinutes] = endTime.split(':').map(Number);
    const newEndDate = new Date(year, month - 1, day, endHours, endMinutes);
    // Update the coffee event
    onUpdate({
      title,
      description,
      date: newDate.toISOString(),
      endTime: newEndDate.toISOString(),
      platform,
      meetingLink: meetingLink || undefined,
      topics: selectedTopics
    });
    onClose();
  };
  const handleDelete = () => {
    if (showDeleteConfirm) {
      onDelete();
      onClose();
    } else {
      setShowDeleteConfirm(true);
    }
  };
  if (!isOpen) return null;
  return <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-489">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-490">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} data-id="element-491"></div>
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full" data-id="element-492">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4" data-id="element-493">
            <div className="flex justify-between items-center mb-4" data-id="element-494">
              <h3 className="text-lg font-medium text-gray-900" data-id="element-495">
                Modifica EIIS Coffee
              </h3>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-500" data-id="element-496">
                <XIcon size={20} data-id="element-497" />
              </button>
            </div>
            <form onSubmit={handleSubmit} data-id="element-498">
              <div className="space-y-4" data-id="element-499">
                <div data-id="element-500">
                  <label htmlFor="title" className="block text-sm font-medium text-gray-700" data-id="element-501">
                    Titolo
                  </label>
                  <input type="text" id="title" value={title} onChange={e => setTitle(e.target.value)} required className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-custom-green focus:border-custom-green sm:text-sm" data-id="element-502" />
                </div>
                <div data-id="element-503">
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700" data-id="element-504">
                    Descrizione
                  </label>
                  <textarea id="description" value={description} onChange={e => setDescription(e.target.value)} required rows={3} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-custom-green focus:border-custom-green sm:text-sm" data-id="element-505"></textarea>
                </div>
                <div className="grid grid-cols-2 gap-4" data-id="element-506">
                  <div data-id="element-507">
                    <label htmlFor="date" className="block text-sm font-medium text-gray-700" data-id="element-508">
                      Data
                    </label>
                    <div className="mt-1 relative rounded-md shadow-sm" data-id="element-509">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-510">
                        <CalendarIcon size={16} className="text-gray-400" data-id="element-511" />
                      </div>
                      <input type="date" id="date" value={date} onChange={e => setDate(e.target.value)} required className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-custom-green focus:border-custom-green sm:text-sm" data-id="element-512" />
                    </div>
                  </div>
                  <div data-id="element-513">
                    <label htmlFor="platform" className="block text-sm font-medium text-gray-700" data-id="element-514">
                      Piattaforma
                    </label>
                    <select id="platform" value={platform} onChange={e => setPlatform(e.target.value as any)} required className="mt-1 block w-full pl-3 pr-10 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-custom-green focus:border-custom-green sm:text-sm" data-id="element-515">
                      <option value="Google Meet" data-id="element-516">Google Meet</option>
                      <option value="Microsoft Teams" data-id="element-517">Microsoft Teams</option>
                      <option value="Zoom" data-id="element-518">Zoom</option>
                      <option value="Altro" data-id="element-519">Altro</option>
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4" data-id="element-520">
                  <div data-id="element-521">
                    <label htmlFor="startTime" className="block text-sm font-medium text-gray-700" data-id="element-522">
                      Ora inizio
                    </label>
                    <div className="mt-1 relative rounded-md shadow-sm" data-id="element-523">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-524">
                        <ClockIcon size={16} className="text-gray-400" data-id="element-525" />
                      </div>
                      <input type="time" id="startTime" value={startTime} onChange={e => setStartTime(e.target.value)} required className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-custom-green focus:border-custom-green sm:text-sm" data-id="element-526" />
                    </div>
                  </div>
                  <div data-id="element-527">
                    <label htmlFor="endTime" className="block text-sm font-medium text-gray-700" data-id="element-528">
                      Ora fine
                    </label>
                    <div className="mt-1 relative rounded-md shadow-sm" data-id="element-529">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-530">
                        <ClockIcon size={16} className="text-gray-400" data-id="element-531" />
                      </div>
                      <input type="time" id="endTime" value={endTime} onChange={e => setEndTime(e.target.value)} required className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-custom-green focus:border-custom-green sm:text-sm" data-id="element-532" />
                    </div>
                  </div>
                </div>
                <div data-id="element-533">
                  <label htmlFor="meetingLink" className="block text-sm font-medium text-gray-700" data-id="element-534">
                    Link alla riunione (opzionale)
                  </label>
                  <input type="url" id="meetingLink" value={meetingLink} onChange={e => setMeetingLink(e.target.value)} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-custom-green focus:border-custom-green sm:text-sm" placeholder="https://meet.google.com/..." data-id="element-535" />
                </div>
                <div data-id="element-536">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-537">
                    Temi del Coffee (seleziona fino a 3)
                  </label>
                  <div className="flex flex-wrap gap-2" data-id="element-538">
                    {topics.map(topic => <button key={topic} type="button" onClick={() => handleTopicToggle(topic)} disabled={selectedTopics.length >= 3 && !selectedTopics.includes(topic)} className={`px-3 py-1 rounded-full text-sm ${selectedTopics.includes(topic) ? 'bg-custom-green text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'} ${selectedTopics.length >= 3 && !selectedTopics.includes(topic) ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-539">
                        {topic}
                      </button>)}
                  </div>
                  {selectedTopics.length === 0 && <p className="mt-1 text-xs text-red-500" data-id="element-540">
                      Seleziona almeno un tema
                    </p>}
                </div>
              </div>
              <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense" data-id="element-541">
                <button type="submit" disabled={selectedTopics.length === 0} className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-custom-green text-base font-medium text-white hover:bg-custom-green/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-custom-green sm:col-start-2 sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-542">
                  Aggiorna
                </button>
                <button type="button" onClick={onClose} className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-custom-green sm:mt-0 sm:col-start-1 sm:text-sm" data-id="element-543">
                  Annulla
                </button>
              </div>
            </form>
            <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-544">
              <div className="flex items-center justify-between" data-id="element-545">
                <div className="flex items-center text-red-600" data-id="element-546">
                  <Trash2Icon size={16} className="mr-2" data-id="element-547" />
                  <span className="text-sm font-medium" data-id="element-548">Elimina evento</span>
                </div>
                <button type="button" onClick={handleDelete} className={`px-3 py-1 rounded-md text-sm ${showDeleteConfirm ? 'bg-red-600 text-white' : 'border border-red-600 text-red-600'}`} data-id="element-549">
                  {showDeleteConfirm ? 'Conferma eliminazione' : 'Elimina'}
                </button>
              </div>
              {showDeleteConfirm && <p className="mt-2 text-xs text-red-500" data-id="element-550">
                  Questa azione non può essere annullata. L'evento verrà
                  eliminato permanentemente.
                </p>}
            </div>
          </div>
        </div>
      </div>
    </div>;
};
export default EditCoffeeModal;